import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { BellRing } from 'lucide-react';
import DataTable from '@components/DataTable';
import type { DataTableColumn } from '@components/DataTable';
import ListPageShell from '@components/ListPageShell';
import { LEGACY_MOBILE_SIZE, LegacyTag, NewFab, pluralItems } from '@components/rep';
import type { SortOrder } from '@components/rep';
import { REP_CHANNELS } from '@constants/index';
import { pluralize, sortRows } from '@utils/index';
import { useAccess } from '@hooks/useAccess';
import { useIsMobile } from '@hooks/useIsMobile';
import { useRealtimeSubscription } from '@hooks/useRealtime';
import { useRepCountiesQuery } from '@services/groupsApi';
import type { Group } from '@/types';
import NewYearDialog from './NewYearDialog';

/** A county row; `pvCount` is a number so the Letters column sorts by value. */
type CountyRow = Group & { pvCount: number };

const lettersTag = (row: CountyRow) => <LegacyTag>{pluralItems(row.pvCount, 'letter')}</LegacyTag>;

const COLUMNS: DataTableColumn<CountyRow>[] = [
  { field: 'pvGroupName', headerName: 'County' },
  { field: 'pvCount', headerName: 'Letters', renderCell: lettersTag },
];

/**
 * State users' Annual Letters of Certification home in the Palmetto 3.0 list layout of the
 * county years overview (LcertOverview): the blue header ("Annual Letter of Certifications",
 * "N results") with the Manage Notifications menu, then a card with the County / Letters table
 * ("N letters" = letters past Incomplete), sortable, A-Z by county first; a card per county on
 * phones. A row opens that county's years overview. Full access adds the "New Year" FAB. The 20
 * REP counties fit one page, so the table is not paged.
 */
const LcertCounties = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile(LEGACY_MOBILE_SIZE);
  const { isFull } = useAccess();
  const { data = [], isLoading, isError, refetch } = useRepCountiesQuery();
  const [newYearOpen, setNewYearOpen] = useState(false);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState('pvGroupName');

  useEffect(() => {
    document.title = 'Annual Letters of Certification - Palmetto REP';
  }, []);

  const onEvent = useCallback(() => void refetch(), [refetch]);
  useRealtimeSubscription(REP_CHANNELS.lcert, onEvent);

  // A failed load shows its message, not the last rows, which may be stale.
  const rows = useMemo(
    () =>
      isError
        ? []
        : sortRows(
            data.map((county) => ({ ...county, pvCount: Number(county.pvCount || 0) })),
            orderBy,
            order,
          ),
    [data, isError, orderBy, order],
  );

  const handleRequestSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const mobileCard = (row: CountyRow) => (
    <div className='rep:flex rep:items-center rep:justify-between rep:gap-2'>
      <span className='rep:text-sm rep:font-semibold rep:text-[#0A0A0A]'>{row.pvGroupName}</span>
      {lettersTag(row)}
    </div>
  );

  const subTitle = isLoading ? 'Loading...' : isError ? '' : pluralize(rows.length, 'result');

  return (
    <div
      className='rep:relative rep:flex rep:h-full rep:w-full rep:flex-col rep:overflow-hidden'
      style={{ paddingBottom: isMobile ? 56 : 48, boxSizing: 'border-box' }}>
      <ListPageShell
        title='Annual Letter of Certifications'
        subtitle={subTitle}
        isMobile={isMobile}
        menuItems={[
          {
            title: 'Manage Notifications',
            icon: <BellRing className='rep:size-5' />,
            onClick: () => navigate('/lcert/notifications'),
          },
        ]}>
        <DataTable
          columns={COLUMNS}
          rows={rows}
          rowKey={(row) => row.pvGroupID}
          loading={isLoading}
          isMobile={isMobile}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          onRowClick={(row) => navigate(`/lcert/county/${row.pvGroupID}`)}
          renderMobileCard={mobileCard}
          emptyMessage={isError ? 'Unable to load counties.' : 'No counties.'}
        />
      </ListPageShell>
      {isFull ? <NewFab label='New Year' bottom={40} onClick={() => setNewYearOpen(true)} /> : null}
      <NewYearDialog open={newYearOpen} onOpenChange={setNewYearOpen} />
    </div>
  );
};

export default LcertCounties;
